import { useMemo, useCallback } from 'react';
import type { MapPin } from '@/components/maps/MapView';
import type { LocationCoordinates } from '@/services/location';
import { calculateDistance } from '@/services/location';
import type {
  DistanceDisplayMode,
  DistanceInfo,
  MapPinDistanceMetadata,
  MapReferencePoint,
} from '@/types/mapReferences';

type UseReferencePointDistancesOptions = {
  pins: MapPin[];
  referencePoints?: MapReferencePoint[];
  primaryReferencePointId?: string;
  distanceDisplayMode?: DistanceDisplayMode;
  currentLocation?: LocationCoordinates | null;
  previousPinCoordinates?: Record<string, LocationCoordinates>;
};

type UseReferencePointDistancesResult = {
  pinsWithDistances: MapPin[];
  primaryReferencePoint?: MapReferencePoint;
  getDistanceToReferencePoint: (
    coordinates: LocationCoordinates,
    referencePointId?: string,
  ) => DistanceInfo | null;
};

const formatDistanceLabel = (meters: number): string =>
  meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;

const getDistanceColor = (meters: number): string => {
  if (meters <= 1000) {
    return '#2ECC71'; // Walkable
  }
  if (meters <= 5000) {
    return '#F39C12'; // Medium travel
  }
  return '#E74C3C'; // Far
};

export function useReferencePointDistances({
  pins,
  referencePoints = [],
  primaryReferencePointId,
  distanceDisplayMode = 'primary_reference',
  currentLocation,
  previousPinCoordinates = {},
}: UseReferencePointDistancesOptions): UseReferencePointDistancesResult {
  // Quantize device location so walking-distance metadata doesn't rebuild every GPS tick
  // (which re-runs the whole pin filter pipeline and can blink large pin sets on native).
  const stableCurrentLocationForDistance = useMemo((): LocationCoordinates | null | undefined => {
    if (!currentLocation) return currentLocation;
    return {
      latitude: Number(currentLocation.latitude.toFixed(4)),
      longitude: Number(currentLocation.longitude.toFixed(4)),
    };
  }, [currentLocation?.latitude, currentLocation?.longitude]);

  const referencePointMap = useMemo(() => {
    const map = new Map<string, MapReferencePoint>();
    referencePoints.forEach((point) => map.set(point.id, point));
    return map;
  }, [referencePoints]);

  const primaryReferencePoint = useMemo(() => {
    if (!referencePoints.length) {
      return undefined;
    }

    if (primaryReferencePointId && referencePointMap.has(primaryReferencePointId)) {
      return referencePointMap.get(primaryReferencePointId);
    }

    return referencePoints.find((point) => point.isPrimary) ?? referencePoints[0];
  }, [referencePoints, primaryReferencePointId, referencePointMap]);

  const buildDistanceInfo = useCallback(
    (coordinates: LocationCoordinates, referencePoint?: MapReferencePoint): DistanceInfo | null => {
      if (!coordinates) {
        return null;
      }

      if (!referencePoint) {
        return null;
      }

      const distance = calculateDistance(coordinates, referencePoint.coordinates);
      return {
        distance,
        distanceFormatted: formatDistanceLabel(distance),
        walkable: distance <= 1000,
        referencePoint,
        travelMode: distance <= 2000 ? 'walking' : undefined,
      };
    },
    [],
  );

  const applyDistanceMetadata = useCallback(
    (pin: MapPin): MapPin => {
      if (!pin.coordinates) {
        return pin;
      }

      let distanceMeta: MapPinDistanceMetadata | undefined;

      if (distanceDisplayMode === 'current_location' && stableCurrentLocationForDistance) {
        const distance = calculateDistance(stableCurrentLocationForDistance, pin.coordinates);
        distanceMeta = {
          distance,
          distanceFormatted: formatDistanceLabel(distance),
          distanceColor: getDistanceColor(distance),
        };
      } else if (distanceDisplayMode === 'previous_activity') {
        const previousCoordinates = previousPinCoordinates[pin.id];
        if (previousCoordinates) {
          const distance = calculateDistance(previousCoordinates, pin.coordinates);
          distanceMeta = {
            distance,
            distanceFormatted: formatDistanceLabel(distance),
            distanceColor: getDistanceColor(distance),
          };
        }
      } else {
        const primaryInfo = buildDistanceInfo(pin.coordinates, primaryReferencePoint);
        const referenceDistances =
          distanceDisplayMode === 'multi_reference'
            ? referencePoints
                .map((point) => buildDistanceInfo(pin.coordinates, point))
                .filter((info): info is DistanceInfo => !!info)
            : primaryInfo
              ? [primaryInfo]
              : [];

        if (referenceDistances.length > 0) {
          distanceMeta = {
            distance: referenceDistances[0].distance,
            distanceFormatted: referenceDistances[0].distanceFormatted,
            distanceColor: getDistanceColor(referenceDistances[0].distance),
            referenceDistances,
          };
        }
      }

      if (!distanceMeta) {
        return pin;
      }

      return {
        ...pin,
        distanceMeta,
      };
    },
    [
      buildDistanceInfo,
      stableCurrentLocationForDistance,
      distanceDisplayMode,
      previousPinCoordinates,
      primaryReferencePoint,
      referencePoints,
    ],
  );

  const pinsWithDistances = useMemo(() => {
    // Always return an array, even if empty, to prevent null/undefined issues
    if (!pins || pins.length === 0) {
      if (__DEV__) {
        console.log(`[ReferencePointDistances] No input pins, returning empty array`);
      }
      return [];
    }
    
    const result = pins.map(applyDistanceMetadata);
    if (__DEV__ && pins.length > 0) {
      console.log(`[ReferencePointDistances] Input pins: ${pins.length}, Output pins: ${result.length}`, {
        pinTypes: pins.map(p => p.type),
        pinsWithCoords: pins.filter(p => p.coordinates).length,
      });
      if (pins.length !== result.length) {
        console.warn(`[ReferencePointDistances] Pin count mismatch!`);
      }
    }
    return result;
  }, [pins, applyDistanceMetadata]);

  const getDistanceToReferencePoint = useCallback(
    (coordinates: LocationCoordinates, referencePointId?: string): DistanceInfo | null => {
      if (!coordinates) {
        return null;
      }

      if (referencePointId) {
        const refPoint = referencePointMap.get(referencePointId);
        if (!refPoint) {
          return null;
        }
        return buildDistanceInfo(coordinates, refPoint);
      }

      return buildDistanceInfo(coordinates, primaryReferencePoint);
    },
    [buildDistanceInfo, primaryReferencePoint, referencePointMap],
  );

  return {
    pinsWithDistances,
    primaryReferencePoint,
    getDistanceToReferencePoint,
  };
}

